import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff, ArrowRight } from "lucide-react";
import Logo from "../components/Logo";
import { useAuth } from "@/context/AuthContext";

const ROLE_HOME = {
  cliente: "/cliente",
  admin: "/admin",
  conductor: "/conductor",
};

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const user = await login(email, password);
      const from = location.state?.from?.pathname;
      const home = ROLE_HOME[user.role] || "/";
      // Solo respeta el "from" si corresponde al panel del rol devuelto por
      // el backend — evita mandar a un cliente a una ruta de /admin solo
      // porque llegó ahí por un link directo antes de loguearse.
      navigate(from && from.startsWith(home) ? from : home, { replace: true });
    } catch (err) {
      if (err.response?.status === 401) {
        setError("Correo o contraseña incorrectos.");
      } else if (err.response?.status === 429) {
        setError("Demasiados intentos. Espera un minuto e intenta de nuevo.");
      } else {
        setError("No se pudo conectar con el servidor. Intenta de nuevo.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0D0E10] text-[#F5F5F5] grain flex items-center justify-center px-6 py-12">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(201,162,39,0.08),transparent_60%)] pointer-events-none" />

      <div className="relative w-full max-w-md">
        <div className="flex justify-center mb-8">
          <Logo size="lg" />
        </div>

        <div className="bg-[#1C1D20] border border-[#2E2F33] rounded-xl p-8" data-testid="login-card">
          <p className="text-[10px] uppercase tracking-brand text-[#C9A227] font-display mb-2">Bienvenido de vuelta</p>
          <h1 className="font-display font-bold text-2xl uppercase tracking-wide">Iniciar sesión</h1>
          <p className="text-sm text-[#A0A0A5] font-body mt-2">Accede a tu panel ejecutivo LEP.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <Field
              icon={Mail}
              label="Correo"
              placeholder="ejecutivo@empresa.pe"
              testId="login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Field
              icon={Lock}
              label="Contraseña"
              type={showPwd ? "text" : "password"}
              placeholder="••••••••"
              testId="login-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              suffix={
                <button
                  type="button"
                  onClick={() => setShowPwd(!showPwd)}
                  className="text-[#A0A0A5] hover:text-[#C9A227] transition"
                  data-testid="login-password-toggle"
                >
                  {showPwd ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              }
            />

            {error && (
              <p className="text-xs text-red-400 font-body" data-testid="login-error">
                {error}
              </p>
            )}

            <div className="flex items-center justify-between text-xs">
              <label className="flex items-center gap-2 text-[#A0A0A5] font-body">
                <input type="checkbox" className="accent-[#C9A227]" data-testid="login-remember" />
                Mantener sesión
              </label>
              <a href="#" className="text-[#C9A227] uppercase tracking-wider font-display" data-testid="login-forgot">
                ¿Olvidaste?
              </a>
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full flex items-center justify-center gap-2 py-3.5 bg-[#C9A227] hover:bg-[#D4AF37] disabled:opacity-60 disabled:cursor-not-allowed text-[#0D0E10] font-display font-bold uppercase tracking-wider text-sm rounded-lg transition"
              data-testid="login-submit-btn"
            >
              {submitting ? "Ingresando…" : "Entrar al panel"}
              <ArrowRight size={14} />
            </button>
          </form>

          <div className="hr-gold my-7" />

          <p className="text-center text-xs text-[#A0A0A5] font-body">
            ¿Aún no tienes cuenta?{" "}
            <Link to="/registro" className="text-[#C9A227] uppercase tracking-wider font-display" data-testid="login-to-register">
              Regístrate
            </Link>
          </p>
        </div>

        <p className="mt-6 text-center text-[10px] text-[#A0A0A5] uppercase tracking-brand font-display">
          <Link to="/" className="hover:text-[#C9A227]">← Volver al inicio</Link>
        </p>
      </div>
    </div>
  );
};

const Field = ({ icon: Icon, label, testId, suffix, ...props }) => (
  <div>
    <label className="text-[10px] uppercase tracking-brand text-[#A0A0A5] font-display block mb-2">{label}</label>
    <div className="flex items-center gap-2 bg-[#0D0E10] border border-[#2E2F33] focus-within:border-[#C9A227] rounded-lg px-3.5 py-3 transition-colors">
      <Icon size={14} className="text-[#C9A227]" />
      <input
        {...props}
        className="flex-1 bg-transparent outline-none text-sm text-[#F5F5F5] font-body placeholder:text-[#5A5B60]"
        data-testid={testId}
      />
      {suffix}
    </div>
  </div>
);

export default Login;
